import React, { useCallback, useEffect, useState } from 'react';
import Image from 'next/image';
import { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';
import { NextButton, PrevButton, usePrevNextButtons } from './ArrowButton';
import useEmblaCarousel from 'embla-carousel-react';
import { PortableTextBlock } from '@sanity/types';
import type { ArbitraryTypedObject } from '@portabletext/types';
import { getSanityImageUrl } from '@/libs/sanity/client';
import { PortableText } from '@portabletext/react';
import { HiOutlineArrowCircleRight } from 'react-icons/hi';
import { usePathname } from 'next/navigation';
import { BlurInH2 } from '@/components/eldora-ui/blur-in-h2';
import Link from 'next/link';

type SlideItem = {
  link?: any;
  title?: string;
  subTitle?: string;
  name?: string;
  image?: any;
  slug?: string;
  content:
    | (PortableTextBlock | ArbitraryTypedObject)
    | (PortableTextBlock | ArbitraryTypedObject)[];
};

type ModuleCarouselProps = {
  slides: number[];
  anchor?: string;
  title?: string;
  modules: {
    slideItems: SlideItem[];
  }[];
};

const ModuleCarousel: React.FC<ModuleCarouselProps> = (props) => {
  const { anchor, title, modules } = props;
  const pathname = usePathname();
  if (!modules || !modules.length) {
    return null;
  }
  const startIndex = modules[0].slideItems.findIndex((item) => {
    return item.slug === pathname;
  });

  const options: EmblaOptionsType = {
    align: 'start',
    dragFree: false,
    containScroll: false,
    loop: true,
    startIndex: startIndex >= 0 ? startIndex : 0,
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [selectedIndex, setSelectedIndex] = useState(options.startIndex);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = usePrevNextButtons(emblaApi);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const onSlideChange = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!emblaApi) return;

    emblaApi.on('select', onSlideChange);

    return () => {
      emblaApi.off('select', onSlideChange);
    };
  }, [emblaApi]);

  return (
    <section
      id={'simplicity-' + anchor}
      key={anchor}
      className="scroll-mt-[150px]"
    >
      <BlurInH2>
        Simplicity® <span className="text-primary font-semibold">{title}</span>
      </BlurInH2>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        <div className="col-span-1 md:col-span-4">
          <div className="flex flex-col justify-between gap-2 xl:h-[304px]">
            <div>
              <h3 className="text-primary font-semibold pb-3 text-lg">
                {modules[0].slideItems[selectedIndex]?.title}
              </h3>
              {modules[0].slideItems[selectedIndex]?.subTitle && (
                <h4 className="font-semibold pb-3 text-md">
                  {modules[0].slideItems[selectedIndex]?.subTitle}
                </h4>
              )}
              <div className="[&>p]:mb-4 [&>ul]:list-disc [&>ul>li]:pb-2 [&>ul>li]:ml-4">
                {modules[0].slideItems[selectedIndex]?.content && (
                  <PortableText
                    value={modules[0].slideItems[selectedIndex]?.content}
                  />
                )}
              </div>
            </div>
            <Link
              href={
                modules[0].slideItems[selectedIndex].link &&
                modules[0].slideItems[selectedIndex]?.link?.internal
                  ? modules[0].slideItems[selectedIndex].link.internal.ref
                      ?.metadata?.slug?.current
                  : '#'
              }
              className="font-semibold border border-primary text-primary p-3 hover:bg-gray-200 w-full flex flex-row justify-between align-baseline !px-3"
            >
              {modules[0].slideItems[selectedIndex].link?.label}
              <HiOutlineArrowCircleRight
                className="text-primary text-lg"
                size="22"
              />
            </Link>
          </div>
        </div>
        <div className="embla col-span-1 md:col-span-8 mt-1">
          <div className="embla__viewport" ref={emblaRef}>
            <div className="embla__container">
              {modules[0].slideItems.map((item: SlideItem, index) => {
                const itemLink = modules[0].slideItems[index];
                if (index === selectedIndex) {
                  return (
                    <div className="embla__slide" key={item.title}>
                      <div className="embla__slide__number flex bg-cyan-50 h-[200px] md:h-[300px]">
                        {item.image && (
                          <Link
                            href={
                              itemLink.link && itemLink?.link?.internal
                                ? itemLink.link.internal.ref?.metadata?.slug
                                    ?.current
                                : '#'
                            }
                          >
                            <Image
                              className="object-cover h-full"
                              src={getSanityImageUrl(item.image)}
                              alt="Your alt text"
                              loading="lazy"
                              width={300}
                              height={600}
                            />
                          </Link>
                        )}
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    className="embla__slide flex flex-col justify-center"
                    key={`${item.title}-${item.subTitle}`}
                  >
                    <Link
                      className="hover:bg-primary/5 transition-colors duration-300"
                      href={
                        itemLink.link && itemLink?.link?.internal
                          ? itemLink.link.internal.ref?.metadata?.slug?.current
                          : '#'
                      }
                    >
                      <div className="embla__slide__number flex flex-col justify-end p-1.5 md:p-2 lg:p-4 text-primary border border-primary h-[150px] md:h-[240px]">
                        {item.title}
                        {item.subTitle && (
                          <>
                            {':'}
                            <br /> {item.subTitle}
                          </>
                        )}
                      </div>
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="embla__controls">
            <div className="embla__buttons">
              <PrevButton
                onClick={onPrevButtonClick}
                disabled={prevBtnDisabled}
              />
              <NextButton
                onClick={onNextButtonClick}
                disabled={nextBtnDisabled}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ModuleCarousel;
